import { searchSuggestionsSchema } from "@nyntax/contracts/schemas/course";
import type {
  AcademyCategory,
  SearchSuggestions,
} from "@nyntax/contracts/schemas/course";

import { readJson } from "./read-json";

/** Nothing to suggest. */
export const NO_SUGGESTIONS: SearchSuggestions = { courses: [], instructors: [] };

/** Most topics the tray lists for a search. */
export const TOPIC_SUGGESTIONS = 3;

/**
 * Loads suggestions for the header search through the same-origin `/v1`
 * proxy. A failure (or an abort) yields no suggestions rather than an error:
 * the search box still submits to the Catalog.
 *
 * @param q - The text typed so far.
 * @param signal - Aborts the request when a newer one starts.
 * @param fetcher - Fetch implementation, for tests.
 * @returns The suggested Courses and instructors.
 */
export const fetchSuggestions = async (
  q: string,
  signal?: AbortSignal,
  fetcher: typeof fetch = fetch
): Promise<SearchSuggestions> => {
  const text = q.trim();

  if (text === "") {
    return NO_SUGGESTIONS;
  }

  try {
    const response = await fetcher(
      `/v1/search/suggestions?q=${encodeURIComponent(text)}`,
      { headers: { accept: "application/json" }, signal }
    );

    if (!response.ok) {
      return NO_SUGGESTIONS;
    }

    const parsed = searchSuggestionsSchema.safeParse(await readJson(response));

    return parsed.success ? parsed.data : NO_SUGGESTIONS;
  } catch {
    return NO_SUGGESTIONS;
  }
};

/**
 * The categories whose translated name contains the text, best first: a name
 * that starts with it, then one with a word that does, then the rest.
 *
 * @param q - The text typed so far.
 * @param categories - Every category with its translated name.
 * @returns Up to three matching categories.
 */
export const matchTopics = (
  q: string,
  categories: readonly { label: string; value: AcademyCategory }[]
): { label: string; value: AcademyCategory }[] => {
  const needle = q.trim().toLocaleLowerCase();

  if (needle === "") {
    return [];
  }

  const rank = (label: string) => {
    const lower = label.toLocaleLowerCase();

    if (lower.startsWith(needle)) {
      return 0;
    }

    return lower.split(/\s+/u).some((word) => word.startsWith(needle)) ? 1 : 2;
  };

  return categories
    .filter(({ label }) => label.toLocaleLowerCase().includes(needle))
    .sort((a, b) => rank(a.label) - rank(b.label))
    .slice(0, TOPIC_SUGGESTIONS);
};

/** A piece of text and whether it is the part that matched. */
export interface HighlightPart {
  match: boolean;
  text: string;
}

/**
 * Splits text around the first case-insensitive match of the search, so the
 * tray can bold the part the visitor typed.
 *
 * @param text - A title or name.
 * @param q - The text typed so far.
 * @returns The pieces in order; one unmatched piece when nothing matches.
 */
export const highlightParts = (text: string, q: string): HighlightPart[] => {
  const needle = q.trim().toLocaleLowerCase();
  const at = needle === "" ? -1 : text.toLocaleLowerCase().indexOf(needle);

  if (at === -1) {
    return [{ match: false, text }];
  }

  return [
    { match: false, text: text.slice(0, at) },
    { match: true, text: text.slice(at, at + needle.length) },
    { match: false, text: text.slice(at + needle.length) },
  ].filter((part) => part.text !== "");
};
